// Sort state in the URL (design system 04-data §1.2.1: "where the view is shareable, the sort MUST
// persist in the URL") and the pure sorting rules every table shares. DOM-free; unit-tested.
import type { SortDirection } from '../../shared/api-types.js';

export interface SortState<K extends string = string> {
  key: K;
  dir: SortDirection;
}

export type SortValueType = 'text' | 'number' | 'date' | 'enum';

export interface SortParamOptions<K extends string> {
  /** Prefix for the `sort`/`dir` keys, so two tables on one page do not collide (06-modules §5.5). */
  namespace?: string;
  /** The keys the table can sort by; anything else in the URL falls back to the default. */
  keys: readonly K[];
  defaultSort: SortState<K>;
}

export function sortParamNames(namespace: string | undefined): { sort: string; dir: string } {
  const prefix = namespace ? `${namespace}_` : '';
  return { sort: `${prefix}sort`, dir: `${prefix}dir` };
}

/** Reads the sort from search params; unknown keys or directions yield the default (fail quiet). */
export function parseSort<K extends string>(params: URLSearchParams, options: SortParamOptions<K>): SortState<K> {
  const names = sortParamNames(options.namespace);
  const rawKey = params.get(names.sort);
  const rawDir = params.get(names.dir);
  const key = rawKey !== null && (options.keys as readonly string[]).includes(rawKey) ? (rawKey as K) : options.defaultSort.key;
  const dir: SortDirection = rawDir === 'asc' || rawDir === 'desc'
    ? rawDir
    : key === options.defaultSort.key ? options.defaultSort.dir : 'asc';
  return { key, dir };
}

/**
 * Writes the sort into a copy of the params. The default sort is written as an absence so a
 * pristine URL stays clean; the visible default indicator is the table's job, not the URL's.
 */
export function writeSort<K extends string>(params: URLSearchParams, sort: SortState<K>, options: SortParamOptions<K>): URLSearchParams {
  const next = new URLSearchParams(params);
  const names = sortParamNames(options.namespace);
  if (sort.key === options.defaultSort.key && sort.dir === options.defaultSort.dir) {
    next.delete(names.sort);
    next.delete(names.dir);
  } else {
    next.set(names.sort, sort.key);
    next.set(names.dir, sort.dir);
  }
  return next;
}

/** The natural first direction for a column type: text A→Z, numbers and dates ascending too. */
export function initialDirection(_type: SortValueType | undefined): SortDirection {
  return 'asc';
}

/**
 * The header-click cycle: a new column starts at its initial direction; the current column flips.
 * It never passes through an "unsorted" state the user could not tell from the default (§1.2.1).
 */
export function toggleSort<K extends string>(current: SortState<K>, key: K, type?: SortValueType): SortState<K> {
  if (current.key !== key) return { key, dir: initialDirection(type) };
  return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}

export type SortValue = string | number | Date | boolean | null | undefined;

function isEmpty(value: SortValue): boolean {
  return value === null || value === undefined || value === '' || (typeof value === 'number' && Number.isNaN(value));
}

function compareValues(a: SortValue, b: SortValue, type: SortValueType): number {
  switch (type) {
    case 'number':
      return Number(a) - Number(b);
    case 'date': {
      const ta = a instanceof Date ? a.getTime() : new Date(String(a)).getTime();
      const tb = b instanceof Date ? b.getTime() : new Date(String(b)).getTime();
      return ta - tb;
    }
    case 'enum':
      return Number(a) - Number(b);
    case 'text':
    default:
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  }
}

/**
 * Stable, type-aware, empties-last sort (04-data §1.2.1). Empty values group at the end in
 * both directions so "no value" never interleaves with data. Enum columns pass a rank number.
 */
export function sortRows<Row>(rows: readonly Row[], valueOf: (row: Row) => SortValue, type: SortValueType, dir: SortDirection): Row[] {
  const decorated = rows.map((row, index) => ({ row, index, value: valueOf(row) }));
  const sign = dir === 'asc' ? 1 : -1;
  decorated.sort((a, b) => {
    const emptyA = isEmpty(a.value);
    const emptyB = isEmpty(b.value);
    if (emptyA && emptyB) return a.index - b.index;
    if (emptyA) return 1;
    if (emptyB) return -1;
    const cmp = compareValues(a.value, b.value, type) * sign;
    return cmp !== 0 ? cmp : a.index - b.index;
  });
  return decorated.map((entry) => entry.row);
}

/** The two direction words of one column, as the mobile sort control, the header's accessible name and the caption say them. */
export interface DirectionWords {
  asc: string;
  desc: string;
}

/**
 * Human labels for the mobile sort control ("Due date, earliest first"). A column whose order the type's words
 * misname — a two-valued kind, where "first to last" says nothing a reader can act on — passes its own
 * (`DataTableColumn.directionWords`: "yours first" / "shared first"), and they win.
 */
export function directionLabels(type: SortValueType | undefined, words?: DirectionWords): DirectionWords {
  if (words) return words;
  switch (type) {
    case 'number':
      return { asc: 'lowest first', desc: 'highest first' };
    case 'date':
      // Why earliest/latest: a due date lies in the future as often as the past, and "oldest first"
      // misreads there; earliest/latest hold for both.
      return { asc: 'earliest first', desc: 'latest first' };
    case 'enum':
      return { asc: 'first to last', desc: 'last to first' };
    case 'text':
    default:
      return { asc: 'A to Z', desc: 'Z to A' };
  }
}
